import { Controller } from '@hotwired/stimulus'
import { Rive } from '../../api/models/types'
import { getComponentContext, lifecycleTrigger } from '../../helpers/controllerHelpers/utils'

export default class extends Controller {
  component: Rive

  connect() {
    const riveScriptUrl = `https://unpkg.com/@rive-app/canvas@2.14.1`
    const riveScript = document.querySelector(`script[src="${riveScriptUrl}"]`)
    const riveScriptFound = !!riveScript
    const { component } = getComponentContext(this)
    this.component = component as Rive
    if (!riveScriptFound) {
      const script = document.createElement('script')
      script.src = riveScriptUrl
      script.onload = () => {
        this.setUpRive()
      }
      document.head.appendChild(script)
    } else {
      this.setUpRive()
    }

    lifecycleTrigger(this, 'mounted')
  }

  disconnect() {
    lifecycleTrigger(this, 'unmounted')
  }

  setUpRive() {
    const riveInitialization = {
      src:
        this.component?.src /*|| this.element.getAttribute('data-components--rive-src-value') */ ||
        '',
      canvas: this.element,
      autoplay: this.component?.autoplay || false,
      artboard: this.component?.artboard || '',
      animation: this.component?.animation || '',
      width: this.component?.width || '1080',
      height: this.component?.height || '1080',
      triggersFromEmbeddables: this.component?.flow_event_to_rive_action_mappings || '',
      captureEvents: this.component?.capture_events || false,
    }

    this.waitForRiveAndInitialize(riveInitialization)
  }

  initRive({
    src,
    canvas,
    autoplay,
    artboard,
    animation,
    width,
    height,
    triggersFromEmbeddables,
    captureEvents,
  }) {
    const r = new rive.Rive({
      src,
      canvas,
      autoplay,
      artboard,
      stateMachines: animation,
      onLoad: (_) => {
        if (triggersFromEmbeddables) {
          const inputs = r.stateMachineInputs(animation) //This doesn't work from a flow action, the stateMachineInputs function is not available
          triggersFromEmbeddables.forEach((trigger) => {
            const button = document.getElementsByClassName(`cid-${trigger.button_id}`)[0]
            if (button) {
              const actionToTrigger = inputs.find((i) => i.name === trigger.action_name)
              button.onclick = () => actionToTrigger.fire()
              console.log(
                `Button "${trigger.button_id}" linked to trigger action "${trigger.action_name}"`
              )
            } else {
              console.log(
                `No button "${trigger.button_id}" found to trigger action "${trigger.action_name}"`
              )
            }
          })
        }
      },
    })

    //if we want to always be checking for events we could remove the "capture_event" property from the component
    if (captureEvents) {
      // Add event listener and provide callback to handle Rive Event
      r.on(rive.EventType.RiveEvent, this.onRiveEventReceived)

      // Can unsubscribe to Rive Events at any time via the off() API like below
      // r.off(EventType.RiveEvent, this.onRiveEventReceived);
    }

    // Adding the rive instance to the DOM element as "rive_instance" attribute
    // this.element.setAttribute('rive_instance', JSON.stringify(r))
    this.element.rive_instance = r

    class FakeResizeObserver {
      observe() {}
      unobserve() {}
      disconnect() {}
    }

    const fileAspectRatio = parseInt(width) / parseInt(height) || 0
    const container = this.element.parentElement
    const MyResizeObserver = ResizeObserver || FakeResizeObserver
    const resizeObserver = new MyResizeObserver(
      this.throttle((entries) => {
        let displayWidth = window.innerWidth
        let displayHeight = window.innerHeight

        if (fileAspectRatio > 0) {
          const displayAspectRatio = displayWidth / displayHeight
          if (fileAspectRatio > displayAspectRatio) {
            displayHeight = displayWidth / fileAspectRatio
          } else {
            displayWidth = displayHeight * fileAspectRatio
          }
        }

        const dpr = Math.min(2, window.devicePixelRatio || 1)

        canvas.width = dpr * displayWidth
        canvas.height = dpr * displayHeight
        canvas.style.width = displayWidth + 'px'
        canvas.style.height = displayHeight + 'px'

        r.startRendering()
        r.resizeToCanvas()
      }, 16)
    )
    resizeObserver.observe(container)
  }

  throttle(f, delay) {
    let timer = 0
    return function (...args) {
      clearTimeout(timer)
      timer = setTimeout(() => f.apply(this, args), delay)
    }
  }

  onRiveEventReceived(riveEvent) {
    console.log('Rive event detected: ', riveEvent)
    const eventData = riveEvent.data
    //Not sure why but 131 is the current rive code for "OpenUrl" events
    if (eventData.type === 131) {
      window.open(eventData.url, eventData.target)
    }
  }

  waitForRiveAndInitialize(riveInitialization) {
    const checkRive = () => {
      if (typeof rive === 'undefined') {
        setTimeout(checkRive, 100)
      } else {
        this.initRive(riveInitialization)
      }
    }
    checkRive()
  }
}
